import { v4 as uuidv4 } from "uuid"

export type ValidateScriptParamType = "string" | "number" | "identifier"

export interface ValidateScriptParam {
  label: string
  type: ValidateScriptParamType
  value: string
  integer?: boolean
  min?: number
}

export interface ValidateScriptRuleItem {
  id: string
  name: string
  params: ValidateScriptParam[]
  variadic?: boolean
  variadicLabel?: string
}

export type ValidateScriptRuleTemplate = Omit<ValidateScriptRuleItem, "id">

export interface ValidateScriptCommandDefinition extends ValidateScriptRuleTemplate {
  cmd: string
  description: string
}

export interface ValidateScriptRuleGroup {
  group: string
  commands: ValidateScriptCommandDefinition[]
}

export interface ValidateScriptStatementParam {
  value: string
  type: ValidateScriptParamType
}

export interface ValidateScriptStatement {
  ruleName: string
  param: ValidateScriptStatementParam[]
}

export const validateScriptRuleGroups: ValidateScriptRuleGroup[] = [
  {
    group: "Length & Presence",
    commands: [
      { cmd: "REQUIRED", name: "REQUIRED", description: "Value must not be empty.", params: [] },
      { cmd: "MIN_LENGTH", name: "MIN_LENGTH", description: "Value length must be at least the given number.", params: [{ label: "Length", type: "number", value: "" }] },
      { cmd: "MAX_LENGTH", name: "MAX_LENGTH", description: "Value length must be at most the given number.", params: [{ label: "Length", type: "number", value: "" }] },
    ],
  },
  {
    group: "Content & Matching",
    commands: [
      { cmd: "ONLY", name: "ONLY", description: "Value must exactly match one of the given values.", params: [{ label: "Value", type: "string", value: "" }], variadic: true, variadicLabel: "Value" },
      { cmd: "ONLY_IGNORE_CASE", name: "ONLY_IGNORE_CASE", description: "Value must match one of the given values, ignoring case.", params: [{ label: "Value", type: "string", value: "" }], variadic: true, variadicLabel: "Value" },
      { cmd: "MUST_CONTAIN", name: "MUST_CONTAIN", description: "Value must contain the given text.", params: [{ label: "Text", type: "string", value: "" }], variadic: true, variadicLabel: "Text" },
      { cmd: "MUST_NOT_CONTAIN", name: "MUST_NOT_CONTAIN", description: "Value must not contain the given text.", params: [{ label: "Text", type: "string", value: "" }], variadic: true, variadicLabel: "Text" },
      { cmd: "STARTS_WITH", name: "STARTS_WITH", description: "Value must start with one of the given prefixes.", params: [{ label: "Prefix", type: "string", value: "" }], variadic: true, variadicLabel: "Prefix" },
      { cmd: "ENDS_WITH", name: "ENDS_WITH", description: "Value must end with one of the given suffixes.", params: [{ label: "Suffix", type: "string", value: "" }], variadic: true, variadicLabel: "Suffix" },
      { cmd: "NO_PREFIX", name: "NO_PREFIX", description: "Value must not start with any of the given prefixes.", params: [{ label: "Prefix", type: "string", value: "" }], variadic: true, variadicLabel: "Prefix" },
      { cmd: "NO_SUFFIX", name: "NO_SUFFIX", description: "Value must not end with any of the given suffixes.", params: [{ label: "Suffix", type: "string", value: "" }], variadic: true, variadicLabel: "Suffix" },
      { cmd: "NO_WHITESPACE", name: "NO_WHITESPACE", description: "Value must not contain whitespace.", params: [] },
    ],
  },
  {
    group: "Text & Format",
    commands: [
      { cmd: "EN_ALPHABET", name: "EN_ALPHABET", description: "Value must contain English letters only.", params: [] },
      { cmd: "NUMERIC_ONLY", name: "NUMERIC_ONLY", description: "Value must contain digits only.", params: [] },
      { cmd: "EN_ALPHABET_NUMERIC", name: "EN_ALPHABET_NUMERIC", description: "Value must contain English letters or digits only.", params: [] },
      { cmd: "EMAIL_FORMAT", name: "EMAIL_FORMAT", description: "Value must be a valid email format.", params: [] },
      { cmd: "MOBILE_NUMBER", name: "MOBILE_NUMBER", description: "Value must be a valid Thai mobile number.", params: [] },
      { cmd: "THAI_ID", name: "THAI_ID", description: "Value must be a valid Thai national ID.", params: [] },
      { cmd: "URL", name: "URL", description: "Value must be a valid URL.", params: [] },
      { cmd: "URL_FORMAT", name: "URL_FORMAT", description: "Value must be a valid URL format.", params: [] },
      { cmd: "JSON_FORMAT", name: "JSON_FORMAT", description: "Value must be valid JSON text.", params: [] },
    ],
  },
  {
    group: "Number",
    commands: [
      { cmd: "NUMBER", name: "NUMBER", description: "Value must be a number such as 10, -10, or 10.5.", params: [] },
      { cmd: "INTEGER", name: "INTEGER", description: "Value must be an integer such as 10 or -10.", params: [] },
      { cmd: "DECIMAL", name: "DECIMAL", description: "Value must include decimal places, such as 10.0.", params: [] },
      { cmd: "GREATER_THAN", name: "GREATER_THAN", description: "Number must be greater than the given value.", params: [{ label: "Value", type: "number", value: "" }] },
      { cmd: "LESS_THAN", name: "LESS_THAN", description: "Number must be less than the given value.", params: [{ label: "Value", type: "number", value: "" }] },
      { cmd: "GREATER_THAN_OR_EQUAL", name: "GREATER_THAN_OR_EQUAL", description: "Number must be greater than or equal to the given value.", params: [{ label: "Value", type: "number", value: "" }] },
      { cmd: "LESS_THAN_OR_EQUAL", name: "LESS_THAN_OR_EQUAL", description: "Number must be less than or equal to the given value.", params: [{ label: "Value", type: "number", value: "" }] },
      { cmd: "NUMBER_RANGE", name: "NUMBER_RANGE", description: "Number must be between min and max.", params: [{ label: "Min", type: "number", value: "" }, { label: "Max", type: "number", value: "" }] },
      { cmd: "MAX_DECIMAL_PLACES", name: "MAX_DECIMAL_PLACES", description: "Number can have at most this many decimal places.", params: [{ label: "Places", type: "number", value: "", integer: true, min: 0 }] },
      { cmd: "DECIMAL_PLACES", name: "DECIMAL_PLACES", description: "Number must have exactly this many decimal places.", params: [{ label: "Places", type: "number", value: "", integer: true, min: 0 }] },
    ],
  },
  {
    group: "Date & Time",
    commands: [
      { cmd: "DATE_VALID", name: "DATE_VALID", description: "Value must be a valid date.", params: [] },
      { cmd: "DATE_FORMAT", name: "DATE_FORMAT", description: "Date must match the given format.", params: [{ label: "Format", type: "string", value: "" }] },
      { cmd: "DATE_BEFORE", name: "DATE_BEFORE", description: "Date must be before the given date.", params: [{ label: "Date", type: "string", value: "" }] },
      { cmd: "DATE_AFTER", name: "DATE_AFTER", description: "Date must be after the given date.", params: [{ label: "Date", type: "string", value: "" }] },
    ],
  },
  {
    group: "Advanced",
    commands: [
      { cmd: "REGULAR_EXPRESSION", name: "REGULAR_EXPRESSION", description: "Value must match the given regular expression.", params: [{ label: "Pattern", type: "string", value: "" }] },
      { cmd: "MATCH_FIELD", name: "MATCH_FIELD", description: "Value must match another field in the same row.", params: [{ label: "Field", type: "string", value: "" }] },
      { cmd: "CUSTOMER_EXISTS", name: "CUSTOMER_EXISTS", description: "Customer must exist by the given lookup key.", params: [{ label: "Key", type: "string", value: "" }] },
    ],
  },
]

export const validateScriptRuleCatalog: ValidateScriptRuleTemplate[] = validateScriptRuleGroups.flatMap((group) =>
  group.commands.map(({ cmd, description, ...rule }) => rule)
)

function cloneRule(template: ValidateScriptRuleTemplate): ValidateScriptRuleItem {
  return {
    ...JSON.parse(JSON.stringify(template)),
    id: uuidv4(),
  }
}

function quoteString(value: string) {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`
}

function unquoteString(raw: string) {
  try {
    return JSON.parse(raw)
  } catch {
    return raw.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, "\\")
  }
}

function splitTopLevel(value: string, separator: string) {
  const result: string[] = []
  let buffer = ""
  let inString = false
  let escaped = false

  for (const char of value) {
    if (inString) {
      buffer += char
      if (escaped) {
        escaped = false
      } else if (char === "\\") {
        escaped = true
      } else if (char === '"') {
        inString = false
      }
      continue
    }

    if (char === '"') {
      inString = true
      buffer += char
      continue
    }

    if (char === separator) {
      result.push(buffer.trim())
      buffer = ""
      continue
    }

    buffer += char
  }

  if (inString) {
    throw new Error("unterminated string literal")
  }

  if (buffer.trim()) {
    result.push(buffer.trim())
  }

  return result
}

function parseArg(raw: string): ValidateScriptStatementParam {
  if (/^"(?:\\.|[^"\\\r\n])*"$/.test(raw)) {
    return { type: "string", value: unquoteString(raw) }
  }

  if (/^-?[0-9]+(?:\.[0-9]+)?$/.test(raw)) {
    return { type: "number", value: raw }
  }

  if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(raw)) {
    return { type: "identifier", value: raw }
  }

  throw new Error(`invalid argument \`${raw}\``)
}

function validateStatement(statement: ValidateScriptStatement) {
  const rule = validateScriptRuleCatalog.find((item) => item.name === statement.ruleName)
  if (!rule) {
    throw new Error(`unknown validate rule \`${statement.ruleName}\``)
  }

  const minArgs = rule.params.length
  const maxArgs = rule.variadic ? Number.POSITIVE_INFINITY : rule.params.length

  if (statement.param.length < minArgs) {
    throw new Error(`validate rule \`${statement.ruleName}\` requires at least ${minArgs} argument(s)`)
  }

  if (statement.param.length > maxArgs) {
    throw new Error(`validate rule \`${statement.ruleName}\` accepts at most ${maxArgs} argument(s)`)
  }

  statement.param.forEach((param, index) => {
    const ruleParam = rule.params[Math.min(index, rule.params.length - 1)]
    if (!ruleParam) {
      return
    }

    if (ruleParam.type === "number" && param.type !== "number") {
      throw new Error(`validate rule \`${statement.ruleName}\` argument ${index + 1} must be a number`)
    }

    if (ruleParam.integer && !/^-?[0-9]+$/.test(param.value)) {
      throw new Error(`validate rule \`${statement.ruleName}\` argument ${index + 1} must be an integer`)
    }

    if (typeof ruleParam.min === "number" && Number(param.value) < ruleParam.min) {
      throw new Error(`validate rule \`${statement.ruleName}\` argument ${index + 1} must be greater than or equal to ${ruleParam.min}`)
    }
  })
}

export function parseValidateScript(script: string): ValidateScriptStatement[] {
  const trimmedScript = script.trim()
  if (!trimmedScript) {
    return []
  }

  if (!trimmedScript.endsWith(";")) {
    throw new Error("validate script rule must end with `;`")
  }

  const statements = splitTopLevel(trimmedScript, ";")
  return statements.map((statement) => {
    const match = /^([a-zA-Z_][a-zA-Z0-9_]*)(?:\((.*)\))?$/.exec(statement)
    if (!match) {
      throw new Error(`invalid rule syntax \`${statement}\``)
    }

    const rawArgs = match[2]?.trim()
    const args = rawArgs ? splitTopLevel(rawArgs, ",").map(parseArg) : []

    const parsedStatement = {
      ruleName: match[1],
      param: args,
    }

    validateStatement(parsedStatement)
    return parsedStatement
  })
}

export function buildValidateScript(rules: ValidateScriptRuleItem[]) {
  return rules.map((rule) => {
    if (!rule.params.length) {
      return `${rule.name};`
    }

    const params = rule.params.map((param) => {
      if (param.type === "string") {
        return quoteString(param.value)
      }

      if (param.type === "number") {
        const value = Number(param.value)
        if (param.integer) {
          return Number.isInteger(value) ? String(value) : "0"
        }
        return Number.isFinite(value) ? String(param.value || 0) : "0"
      }

      return param.value || "_"
    })

    return `${rule.name}(${params.join(", ")});`
  }).join("\n")
}

export function createValidateScriptRuleItem(name: string) {
  const template = validateScriptRuleCatalog.find((item) => item.name === name)
  return template ? cloneRule(template) : null
}

export function hydrateValidateScriptRules(script: string) {
  const statements = parseValidateScript(script)

  return statements.flatMap((statement) => {
    const template = validateScriptRuleCatalog.find((item) => item.name === statement.ruleName)
    if (!template) {
      return []
    }

    const rule = cloneRule(template)
    rule.params = statement.param.map((param, index) => ({
      label: rule.params[index]?.label || rule.variadicLabel || "Value",
      type: rule.params[index]?.type || param.type,
      value: param.value,
    }))

    return rule
  })
}
